/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useNotifications.ts
 🗓️ Created: 2025-01-05
 🧩 Description:
    Custom hook for managing in-app notifications using Sonner.
    Provides a simple API for showing toast notifications.

 🔧 Notes:
    - Uses Sonner toast library for elegant notifications
    - Supports success, error, warning, and info types
    - Auto-dismiss with configurable duration
    - Backward compatible API with legacy notification system

 🔁 Last Updated: 2026-01-22 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { toast } from 'sonner';
import { useCallback } from 'react';

/**
 * Interface for notification data structure (backward compatibility)
 * @interface NotificationData
 */
export interface NotificationData {
  /** Unique identifier for the notification */
  id: string;
  /** Type of notification determining visual styling and icon */
  type: 'success' | 'error' | 'info' | 'warning';
  /** Message text to display to the user */
  message: string;
  /** Duration in milliseconds before auto-closing (optional) */
  duration?: number;
}

/**
 * Custom hook for managing notifications using Sonner
 * Provides a centralized way to handle success, error, warning, and info notifications
 * 
 * @returns {Object} Object containing notification control functions
 */
export const useNotifications = () => {
  // Empty array for backward compatibility - Sonner manages state internally
  const notifications: NotificationData[] = [];

  /**
   * Adds a new notification using Sonner toast
   * 
   * @param {Omit<NotificationData, 'id'>} notification - Notification data without ID
   */
  const addNotification = useCallback((notification: Omit<NotificationData, 'id'>) => {
    const { type, message, duration } = notification;

    switch (type) {
      case 'success':
        toast.success(message, { duration: duration || 3000 });
        break;
      case 'error':
        toast.error(message, { duration: duration || 5000 });
        break;
      case 'warning':
        toast.warning(message, { duration: duration || 4000 });
        break;
      case 'info':
        toast.info(message, { duration: duration || 3000 });
        break;
      default:
        toast(message, { duration: duration || 3000 });
    }
  }, []);

  /**
   * Remove notification - Sonner handles this automatically
   * Kept for backward compatibility
   */
  const removeNotification = useCallback((_id: string) => {
    // Sonner manages dismissal automatically
  }, []);

  /**
   * Clear all notifications
   */
  const clearAll = useCallback(() => {
    toast.dismiss();
  }, []);

  /**
   * Convenience function to show a success notification
   * 
   * @param {string} message - Success message to display
   * @param {number} [duration=3000] - Duration in milliseconds (default: 3 seconds)
   */
  const showSuccess = useCallback((message: string, duration = 3000) => {
    toast.success(message, { duration });
  }, []);

  /**
   * Convenience function to show an error notification
   * 
   * @param {string} message - Error message to display
   * @param {number} [duration=5000] - Duration in milliseconds (default: 5 seconds)
   */
  const showError = useCallback((message: string, duration = 5000) => {
    toast.error(message, { duration });
  }, []);

  /**
   * Convenience function to show a warning notification
   * 
   * @param {string} message - Warning message to display
   * @param {number} [duration=4000] - Duration in milliseconds (default: 4 seconds)
   */
  const showWarning = useCallback((message: string, duration = 4000) => {
    toast.warning(message, { duration });
  }, []);

  /**
   * Convenience function to show an info notification
   * 
   * @param {string} message - Info message to display
   * @param {number} [duration=3000] - Duration in milliseconds (default: 3 seconds)
   */
  const showInfo = useCallback((message: string, duration = 3000) => {
    toast.info(message, { duration });
  }, []);

  return {
    notifications,
    addNotification,
    removeNotification,
    clearAll,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };
};
